<script lang="ts" setup>
import { listAmenities } from '__generated__/listAmenities'
import { eventAttributesTypes } from '__generated__/eventAttributesTypes'
import { getEvents_listEvents_ListEventsOutput_events_admissions } from '__generated__/getEvents'
import {
  ListEventsFilter,
  SortOrder,
  EventSortField,
  ListEventsSort,
  ClubSortField,
EventPurchaseType
} from '__generated__/globalTypes'
import { listClubs } from '__generated__/listClubs'

import { ref, toRefs, watch } from 'vue'
import { useRouter, useRoute, onBeforeRouteUpdate } from 'vue-router'
import { useHead } from '@vueuse/head'

import { LIST_CLUBS_QUERY } from '@base/api/queries/listClubs'
import { useClient } from '@base/api/useClient'

import { createSlug } from '@base/lib/createSlug'
import { ugcImageSrc } from '@base/lib/ugcImageSrc'
import { createDistanceContent } from '@base/lib/createDistanceContent'

import { Card, CardType } from '@base/_types/Card'
import { ToastSemantic } from '@base/_types/Toast'
import { useToastsStore } from '@base/store/toastsStore'

import CardList from '@base/components/CardList/CardList.vue'
import ImageWrapper from '@base/components/ImageWrapper/ImageWrapper.vue'
import USHeroBanner from '@base/components/USHeroBanner/USHeroBanner.vue'
import USInputSort from '@base/components/USInputSort/USInputSort.vue'
import USPagination from '@base/components/USPagination/USPagination.vue'
import USIcon from '@base/components/USIcon/USIcon.vue'

import EventSearchBar from '@player/_components/EventSearchBar/EventSearchBar.vue'

import { playerTitle } from '@player/_lib/playerTitle'

import heroBannerImage from '@player/_assets/events-cover.jpg'

import { LIST_AMENITIES } from '@player/courses/api/queries/listAmenities'
import { LIST_COURSES } from '@player/courses/api/queries/listCourses'
import { clubsRouteNames } from '@player/clubs/router/routeNames'
import { useEventsStore } from '@player/events/store/eventsStore'
import { eventsRouteNames } from '@player/events/router/routeNames'
import { EVENT_ATTRIBUTES_TYPES } from '@player/events/api/queries/eventAttributesTypes'

import imageClubLogoPlaceholder from '@player/_assets/club-logo-placeholder.jpg'
import imageEventPlaceholder from '@player/_assets/event-placeholder.jpg'

import { props as propDefinition } from './props'

type EventSearchBarSelectType = {
  label: string
  value: string
}

type EventSearchEvent = {
  startDate: string | null
  endDate: string | null
  lat: number | null
  lon: number | null
  loc: string | null
  type: string[] | []
  clubs: string[] | []
  amenitiesValue: string[] | []
}

// Props
const props = defineProps(propDefinition)

// Static Variables
const limit = 10
const client = useClient()
const router = useRouter()
const route = useRoute()
const eventsStore = useEventsStore()
const apiKey = import.meta.env.VITE_GCP_GEOCODING_API_KEY
const toastsStore = useToastsStore()

useHead({
  title: playerTitle('Events')
})

const sortOptions: any[] = [
  {
    label: 'A-Z',
    value: EventSortField.NAME
  },
  {
    label: 'Date Start - Ascending',
    value: EventSortField.DATE_START
  },
  {
    label: 'Date Start - Descending',
    value: EventSortField.DATE_START + '-desc'
  },
  {
    label: 'Near me',
    value: EventSortField.DISTANCE
  },
  {
    label: 'Price Low to High',
    value: EventSortField.ENTRY_FEE
  },
  {
    label: 'Reviews High to Low',
    value: 'club_rating_dsc'
  }
]

// Inputs
const currentSort = ref<string>(EventSortField.DATE_START + '-desc')
const currentPage = ref<number>(1)
const start = ref<Date | undefined>(undefined)
const end = ref<Date | undefined>(undefined)
const lat = ref<number | null>()
const lon = ref<number | null>()
const loc = ref<string | null>()
const clubIds = ref<string[]>([])
const amenities = ref<string[]>([])
const eventTypes = ref<string[] | undefined>()
const loading = ref<boolean>(false)

const { sort, page } = toRefs(props)
const today = new Date(new Date().setHours(0, 0, 0))

currentSort.value = sort?.value || EventSortField.DATE_START
currentPage.value = page?.value || 1
start.value = props.start ? new Date(props.start) : today
end.value = props.end ? new Date(props.end) : undefined
lat.value = props.lat ? props.lat : undefined
lon.value = props.lon ? props.lon : undefined
loc.value = props.loc || undefined
clubIds.value = props.clubs ? props.clubs.split(',') : []
amenities.value = props.amenities ? props.amenities.split(',') : []
eventTypes.value = props.types ? props.types.split(',') : undefined

// Outputs
const { events, count, serverError } = toRefs(eventsStore)
const maxPage = ref<number>(1)
const clubIdsFromCourses = ref<string[]>([])
const clubDataFromCourses = ref<EventSearchBarSelectType[]>([])
const clubAmenities = ref<EventSearchBarSelectType[]>([])
const eventTypesList = ref<EventSearchBarSelectType[]>([])
const results = ref<string>('')

const eventSearchBarProps = {
  apiKey: apiKey,
  buttonLabel: 'Explore Events',
  amenitiesTypeSelect: clubAmenities.value,
  golfClubSelect: clubDataFromCourses.value,
  eventTypeSelect: eventTypesList.value,
  clubs: clubIds.value,
  amenities: amenities.value,
  eventType: eventTypes.value,
  startDate: start.value?.toString(),
  endDate: end.value?.toString(),
  lat: lat.value,
  lon: lat.value,
  loc: loc.value
}

// Helper Functions
const reset = () => {
  serverError.value = false
  events.value = []
  count.value = 0
  loading.value = false
  results.value = ''
}

const strToSort = (str: string | undefined): [EventSortField, SortOrder] => {
  if (str === EventSortField.NAME) {
    return [EventSortField.NAME, SortOrder.ASC]
  } else if (str === EventSortField.DISTANCE) {
    return [EventSortField.DISTANCE, SortOrder.ASC]
  } else if (str === EventSortField.ENTRY_FEE) {
    return [EventSortField.ENTRY_FEE, SortOrder.ASC]
  } else if (str === EventSortField.DATE_START) {
    return [EventSortField.DATE_START, SortOrder.ASC]
  } else if (str === EventSortField.DATE_START + '-desc') {
    return [EventSortField.DATE_START, SortOrder.DESC]
  }
  return [EventSortField.DATE_START, SortOrder.DESC]
}

const handleCount = () => {
  results.value = ''
  let minResults = 0
  let maxResults = 0
  if (count.value > 0) {
    if (currentPage.value - 1 > 0) {
      minResults = (currentPage.value - 1) * limit
    } else {
      minResults = currentPage.value - 1
    }

    if (count.value < currentPage.value * limit) {
      maxResults = count.value
    } else {
      maxResults = Math.ceil(currentPage.value * limit)
    }
    results.value += `${minResults} - ${maxResults}`
  }

  results.value += ` of ${count.value}`
}

const handleSort = (e: [string]) => {
  if (e[0] === EventSortField.NAME) {
    currentSort.value = EventSortField.NAME
    router.replace({ query: { ...route.query, sort: EventSortField.NAME } })
  } else if (e[0] === EventSortField.DISTANCE) {
    currentSort.value = EventSortField.DISTANCE
    router.replace({ query: { ...route.query, sort: EventSortField.DISTANCE } })
  } else if (e[0] === EventSortField.ENTRY_FEE) {
    currentSort.value = EventSortField.ENTRY_FEE
    router.replace({
      query: { ...route.query, sort: EventSortField.ENTRY_FEE }
    })
  } else if (e[0] === EventSortField.DATE_START) {
    currentSort.value = EventSortField.DATE_START
    router.replace({
      query: { ...route.query, sort: EventSortField.DATE_START }
    })
  } else if (e[0] === EventSortField.DATE_START + '-desc') {
    currentSort.value = EventSortField.DATE_START + '-desc'
    router.replace({
      query: { ...route.query, sort: undefined }
    })
  }
}

const handlePagination = (page: number) => {
  currentPage.value = page
  router.push({ params: { page: page } })
}

const generateClubLogo = (logo: string, clubName: string) => {
  return {
    src: logo ? ugcImageSrc(logo || '')[0] : imageClubLogoPlaceholder,
    srcset: logo ? ugcImageSrc(logo || '')[0] : imageClubLogoPlaceholder,
    alt: `The logo for ${clubName}`
  }
}

const generateEventFeaturedImage = (
  clubName: string,
  eventName: string,
  featuredImage: string[]
) => {
  return {
    src:
      featuredImage && featuredImage[0]
        ? ugcImageSrc(featuredImage[0] || '')[0]
        : imageEventPlaceholder,
    srcset:
      featuredImage && featuredImage[0]
        ? ugcImageSrc(featuredImage[0] || '')[0]
        : imageEventPlaceholder,
    alt: `The featuredImage for the ${eventName} event at ${clubName}`
  }
}

const createEventFee = (
  admissions: getEvents_listEvents_ListEventsOutput_events_admissions[] | null
) => {
  if (admissions && admissions.length) {
    let lowest = 0
    admissions.forEach((admission) => {
      if (admission.price < lowest && lowest !== 0) {
        lowest = admission.price
      } else {
        lowest = admission.price
      }
    })
    return `$${lowest}${admissions.length > 1 ? '+' : ''}`
  }
}

const formatEventCards = () => {
  let formattedData = <Card[]>[]
  events.value.forEach((event) => {
    if (event.club.__typename === 'GetClubOutput' && event.club.club !== null) {
      let clubLogo = generateClubLogo(
        event.club.club.logo || '',
        event.club.club.clubName
      )

      let eventFeaturedImage = generateEventFeaturedImage(
        event.club.club.clubName,
        event.name,
        event.gallery || []
      )

      formattedData.push({
        type: CardType.CardListingEvent,
        data: {
          club: {
            name: event.club.club.clubName,
            rating: event.club.club.averageRating || 0,
            logo: clubLogo,
            to: {
              name: clubsRouteNames.ClubSingle,
              params: {
                slug: createSlug(event.club.club.clubName, event.club.club.slug)
              }
            }
          },
          event: {
            name: event.name,
            location: event.club.club.city || '',
            distance:
              event.club.club.addressGeom && lat.value && lon.value
                ? createDistanceContent(event.club.club.addressGeom, {
                    lat: lat.value,
                    lon: lon.value
                  })
                : '',
            fee: event.purchaseType === EventPurchaseType.UPSWING ? createEventFee(event.admissions) || '' : event.externalPriceRange || '',
            start: new Date(event.dateStart),
            exclusive: event.exclusive || false,
            featuredImage: eventFeaturedImage,
            to: {
              name: eventsRouteNames.EventSingle,
              params: {
                slug: createSlug(event.name, event.slug)
              }
            }
          }
        }
      })
    }
  })

  return formattedData
}

// Query Functions

const fetchListOfGolfCourses = async () => {
  try {
    let { data } = await client.query<listClubs>({
      query: LIST_CLUBS_QUERY,
      variables: {
        filter: {},
        sortField: ClubSortField.NAME,
        sortOrder: SortOrder.ASC,
        offset: 0,
        limit: 0
      },
      fetchPolicy: 'cache-first'
    })

    if (data.listClubs.__typename === 'ListClubsOutput') {
      data.listClubs.clubs.forEach((club) => {
        if (!clubIdsFromCourses.value.includes(club.id)) {
          clubIdsFromCourses.value.push(club.id)
          clubDataFromCourses.value.push({
            label: club.clubName,
            value: club.id
          })
        }
      })
    }
  } catch (e) {
    console.error(e)
    serverError.value = true
  }
}

const fetchListOfAmenities = async () => {
  try {
    const { data } = await client.query<listAmenities>({
      query: LIST_AMENITIES,
      fetchPolicy: 'no-cache'
    })

    if (!data || data.fetchAmenities.__typename !== 'AttributesOutput') {
      return
    }

    data.fetchAmenities.attributes.forEach((item) => {
      clubAmenities.value.push({
        label: item.name,
        value: item.id
      })
    })
  } catch (e) {
    console.error(e)
    serverError.value = true
  }
}

const fetchListOfClubs = async () => {
  try {
    let { data } = await client.query<listClubs>({
      query: LIST_CLUBS_QUERY,
      variables: {
        filter: {},
        sortField: ClubSortField.NAME,
        sortOrder: SortOrder.ASC,
        offset: 0,
        limit: 0
      },
      fetchPolicy: 'cache-first'
    })

    if (data.listClubs.__typename === 'ListClubsOutput') {
      data.listClubs.clubs.forEach((club) => {
        if (!clubIdsFromCourses.value.includes(club.id)) {
          clubIdsFromCourses.value.push(club.id)
          clubDataFromCourses.value.push({
            label: club.clubName,
            value: club.id
          })
        }
      })
    }
  } catch (e) {
    console.error(e)
    serverError.value = true
  }
}

const fetchListOfEventTypes = async () => {
  try {
    const { data } = await client.query<eventAttributesTypes>({
      query: EVENT_ATTRIBUTES_TYPES,
      fetchPolicy: 'no-cache'
    })

    if (!data || data.fetchEventTypes.__typename !== 'AttributesOutput') {
      return
    }

    data.fetchEventTypes.attributes.forEach((item) => {
      eventTypesList.value.push({
        label: item.name,
        value: item.id
      })
    })
  } catch (e) {
    console.error(e)
    serverError.value = true
  }
}

const fetchData = async () => {
  reset()
  loading.value = true
  try {
    if (!props.lat && !props.lon) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            lat.value = position.coords.latitude
            lon.value = position.coords.longitude
          },
          () => {
            console.error('user declined')
          }
        )
      } else {
        toastsStore.pushToast({
          content: 'Your browser dose not support geolocation',
          icon: 'exclamation-square',
          semantic: ToastSemantic.Error
        })
      }
    } else {
      lat.value = props.lat
      lon.value = props.lon
    }

    const filter: ListEventsFilter = {
      dateStartBefore: end.value,
      dateStartAfter: start.value,
      addressGeom:
        lat.value !== undefined &&
        lon.value !== undefined &&
        lat.value !== null &&
        lon.value !== null
          ? { lat: lat.value, lon: lon.value }
          : undefined,
      eventType: eventTypes.value ? eventTypes.value : undefined,
      clubIds: clubIds.value,
      amenities: amenities.value
    }

    const [sortField, sortOrder] = strToSort(currentSort.value)

    const sort: ListEventsSort[] = [
      {
        sortField: sortField,
        sortOrder: sortOrder
      }
    ]

    const offset = (currentPage.value - 1) * limit

    await eventsStore.listEvents(client, filter, sort, offset, limit)

    if (serverError.value === true) return
    maxPage.value = Math.ceil(count.value / limit)
    handleCount()
  } catch (e) {
    console.error(e)
    serverError.value = true
  } finally {
    loading.value = false
  }
}

const handleEventSearch = (e: EventSearchEvent) => {
  const fmtStart = e.startDate ? new Date(e.startDate) || undefined : new Date()
  if (fmtStart) fmtStart.setHours(0, 0, 0)

  let fmtEnd = e.endDate ? new Date(e.endDate) || undefined : undefined
  if (fmtEnd) fmtEnd.setHours(23, 59, 59)

  start.value = fmtStart
  end.value = fmtEnd
  lat.value = e.lat
  lon.value = e.lon
  loc.value = e.loc
  eventTypes.value = e.type
  clubIds.value = e.clubs
  amenities.value = e.amenitiesValue

  const query = {
    start: fmtStart ? fmtStart.toISOString() : undefined,
    end: fmtEnd ? fmtEnd.toISOString() : undefined,
    lat: lat.value || undefined,
    lon: lon.value || undefined,
    loc: loc.value || undefined,
    clubs: e.clubs || undefined,
    amenities: amenities.value || undefined,
    types: eventTypes.value || undefined,
    sort: undefined as string | undefined
  }

  if (lat.value || lon || loc) query.sort = EventSortField.DISTANCE
  router.push({ query })

  fetchData()
}

// Fetch Data when the URL changes the props

onBeforeRouteUpdate(async () => {
  await fetchData()
  loading.value = true
  loading.value = false
})

watch(
  () => props.page,
  (newVal) => {
    currentPage.value = newVal || 1
    fetchData()
  }
)

watch(
  () => props.sort,
  (newVal) => {
    currentSort.value = newVal || EventSortField.DATE_START + '-desc'
    fetchData()
  }
)

// Fetch data when the view is first rendered
await fetchListOfEventTypes()
await fetchListOfAmenities()
//await fetchListOfClubs()
fetchData()
</script>

<template>
  <div class="events">
    <USHeroBanner class="events__hero-banner">
      <div class="overlay"></div>
      <ImageWrapper
        :src="heroBannerImage"
        object-fit="cover"
        object-position="50% 50%"
        height="100%"
      />
      <div class="events__hero-banner-content">
        <div class="grid__row">
          <div class="grid__column grid__column--12">
            <div class="font-color--neutral-a5 text-align--center">
              <p class="display--d1">Find Your Fun</p>
            </div>
          </div>
        </div>
      </div>
    </USHeroBanner>
    <div class="grid__row events__row-containing-searchbar">
      <div class="grid__column grid__column--12">
        <div class="events__searchbar-wrapper">
          <EventSearchBar
            v-bind="eventSearchBarProps"
            @submitEventSearch="handleEventSearch($event)"
          />
        </div>
      </div>
    </div>
    <template v-if="loading">
      <div class="grid__row">
        <div class="grid__column grid__column--12">
          <USIcon name="spinner" css-diameter="22px" class="events__loading" />
        </div>
      </div>
    </template>
    <template v-else-if="events && events.length">
      <div class="grid__row">
        <div class="grid__column grid__column--12">
          <div class="events__listing-controls margin-top--6 margin-bottom--8">
            <p class="body--p2--regular font-color--neutral-a3">
              {{ results }}
            </p>
            <USInputSort
              :model-value="[currentSort]"
              select-text="Sort"
              :options="sortOptions"
              @update:model-value="handleSort"
            />
          </div>
        </div>
      </div>
      <CardList :cards="formatEventCards(events)" />
      <USPagination
        v-if="maxPage > 1"
        :min="1"
        :max="maxPage"
        :current="currentPage"
        class="margin-top--5"
        @page-goto="handlePagination"
        @page-next="handlePagination"
        @page-previous="handlePagination"
      />
    </template>
    <template v-else>
      <div class="grid__row">
        <div class="grid__column grid__column--12">
          <p class="body-p1--regular text-align--center margin-top--6">
            Hmmm! we don't seem to have any events that meet your search
            criteria. Try another search.
          </p>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint';

$search-bar-offset-sm: 30px;
$search-bar-offset-lg: 50px;

.events {
  &__hero-banner {
    margin-top: calc(-1 * var(--spacing--5));

    .overlay {
      z-index: 1;
      position: absolute;
      width: 100%;
      height: 100%;
      background-color: black;
      opacity: .15;
    }
  }

  &__hero-banner-content {
    z-index: 2;
    left: 0;
    position: absolute;
    top: calc(50%);
    transform: translateY(-50%);
    width: 100%;
  }

  &__row-containing-searchbar {
    margin-top: #{$search-bar-offset-sm * -1};

    @include breakpoint.min-width(breakpoint.$breakpoint--9) {
      margin-top: #{$search-bar-offset-lg * -1};
    }
  }

  &__listing-controls {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  &__loading {
    animation: rotation 1s infinite ease-in-out;
    margin: 0 auto;
  }

  @keyframes rotation {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(359deg);
    }
  }
}
</style>
